import { useCallback, useEffect } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { useAuthStore, decodeJwtPayload } from '@/stores/authStore';
import type { LoginRequest, LoginResponse } from '@/types';

/**
 * Module-level singleton refresh timer. Auth is one logical session per app, so
 * a single timer (NOT a per-hook-instance useRef) prevents duplicate refreshes
 * when useAuth is mounted in more than one component (e.g. Sidebar + LoginPage)
 * or when login() arms one and the boot effect would arm another. OP-153.
 */
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Authentication hook providing login, logout, and token refresh.
 * The access token is persisted (localStorage, `css-agent-auth`) and, on
 * hydrate, kept only while its `exp` is in the future (see authStore).
 */
export function useAuth() {
  const { setAuth, updateToken, logout, isAuthenticated, user, token } = useAuthStore();

  /**
   * Schedule a token refresh ~60s before expiry. Replaces any existing timer
   * (singleton), so it is safe to call from login() and the boot effect.
   */
  const scheduleRefresh = useCallback(
    (expiresAt: string) => {
      clearRefreshTimer();
      const expiresMs = new Date(expiresAt).getTime();
      const refreshIn = Math.max(expiresMs - Date.now() - 60_000, 5_000);

      const attemptRefresh = async (): Promise<void> => {
        const state = useAuthStore.getState();
        if (!state.refreshToken) return;
        const response = await axiosClient.post<LoginResponse>(API.AUTH.REFRESH, {
          refreshToken: state.refreshToken,
        });
        updateToken(response.data.accessToken);
        const refreshExpiresAt = new Date(Date.now() + response.data.expiresIn * 1000).toISOString();
        scheduleRefresh(refreshExpiresAt);
      };

      refreshTimer = setTimeout(async () => {
        try {
          await attemptRefresh();
        } catch {
          // OP-335 — a single flaky moment (or a wake-from-sleep race where
          // the network isn't back up yet) used to log the agent out
          // immediately even though the refresh token was still perfectly
          // valid. Retry once after a short delay before giving up; reuses
          // the singleton timer so logout() still cancels a pending retry.
          refreshTimer = setTimeout(async () => {
            try {
              await attemptRefresh();
            } catch {
              logout();
            }
          }, 5_000);
        }
      }, refreshIn);
    },
    [updateToken, logout]
  );

  /**
   * Log in with credentials. On success, stores the JWT and arms the refresh.
   */
  const login = useCallback(
    async (credentials: LoginRequest): Promise<void> => {
      const response = await axiosClient.post<LoginResponse>(API.AUTH.LOGIN, credentials);
      const { accessToken, refreshToken, expiresIn } = response.data;
      setAuth(accessToken, refreshToken);
      const expiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();
      scheduleRefresh(expiresAt);
    },
    [setAuth, scheduleRefresh]
  );

  /**
   * Complete a forced first-login password change (PRE-session). The account
   * holds a temporary password, so login answered 403 `password_reset_required`
   * and issued no token; this posts the new password to the anonymous
   * change-password endpoint and, on success, establishes the returned session
   * exactly like login(). `tenantId` goes in the X-Tenant-Id header because no
   * session exists for the request interceptor to read it from, and the body
   * carries none. Navigation is the caller's job (the hook stays router-free).
   */
  const changePassword = useCallback(
    async (
      email: string,
      currentPassword: string,
      newPassword: string,
      tenantId: string
    ): Promise<void> => {
      const response = await axiosClient.post<LoginResponse>(
        API.AUTH.CHANGE_PASSWORD,
        { email, currentPassword, newPassword },
        { headers: { 'X-Tenant-Id': tenantId } }
      );
      const { accessToken, refreshToken, expiresIn } = response.data;
      setAuth(accessToken, refreshToken);
      const expiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();
      scheduleRefresh(expiresAt);
    },
    [setAuth, scheduleRefresh]
  );

  /**
   * Complete a marketing-site sign-in hand-off (PRE-session): redeem the single-use code from
   * /auth/handoff and establish the returned session exactly like login(), refresh timer included.
   * `tenantId` is the canonical id the marketing site received; it goes in X-Tenant-Id because no
   * session exists yet. Rejects on any refusal. Navigation is the caller's job.
   */
  const completeHandoff = useCallback(
    async (code: string, tenantId: string): Promise<void> => {
      const response = await axiosClient.post<LoginResponse>(
        API.AUTH.HANDOFF_REDEEM,
        { code },
        { headers: { 'X-Tenant-Id': tenantId } }
      );
      const { accessToken, refreshToken, expiresIn } = response.data;
      setAuth(accessToken, refreshToken);
      const expiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();
      scheduleRefresh(expiresAt);
    },
    [setAuth, scheduleRefresh]
  );

  /**
   * OP-153 — arm the proactive refresh on app boot when a valid token was
   * rehydrated from storage. Previously only login() armed the timer, so a page
   * reload left no timer and the first 401 forced a hard logout instead of a
   * silent refresh. The singleton timer makes this idempotent.
   */
  useEffect(() => {
    const state = useAuthStore.getState();
    if (state.isAuthenticated && state.token && state.refreshToken && !refreshTimer) {
      try {
        const payload = decodeJwtPayload(state.token);
        if (typeof payload.exp === 'number') {
          scheduleRefresh(new Date(payload.exp * 1000).toISOString());
        }
      } catch {
        /* invalid token — the axios interceptor will 401 and handle it */
      }
    }
  }, [scheduleRefresh]);

  /**
   * Log out: cancel the refresh timer, revoke the refresh token SERVER-SIDE
   * (best-effort — OP-153, so a stolen refresh token can't be reused after the
   * agent signs out), then clear local state.
   */
  const handleLogout = useCallback(async () => {
    clearRefreshTimer();
    try {
      const refreshToken = useAuthStore.getState().refreshToken;
      await axiosClient.post(API.AUTH.LOGOUT, refreshToken ? { refreshToken } : {});
    } catch {
      /* best-effort — clear local state even if the revoke call fails */
    }
    logout();
  }, [logout]);

  return {
    login,
    changePassword,
    completeHandoff,
    logout: handleLogout,
    isAuthenticated,
    user,
    token,
  };
}
